import { useMemo } from "react";
import {
  diamondSquareRidge,
  ridgePathSmoothCurves,
  smoothRidgeY,
} from "./mountainRidge";
import "./MountainRangeBackground.css";

const W = 1440;
const H = 580;

/** viewBox size of every ridge path below. */
export const MOUNTAIN_RANGE_SIZE = { width: W, height: H } as const;

const GRID_EXPONENT = 7;

/** Softens jagged diamond–square output; reads like treeline / rounded crests */
const RIDGE_SMOOTH_PASSES = 2;
const RIDGE_NEIGHBOR_BLEND = 0.14;

/** Single shared profile (diamond–square + Rockies) — login & home hero */
export const MOUNTAIN_RANGE_LAYERS = [
  {
    seed: 0x5f3759df,
    yLeft: 388,
    yRight: 372,
    roughness: 0.42,
    roughnessDecay: 0.52,
    rowT: 0.16,
    peakSpan: 62,
    rockies: {
      peakSharpness: 1.06,
      envelopeStrength: 0.72,
      spikeIntensity: 0.1,
      spikeCount: 4,
      serration: 0.028,
    },
  },
  {
    seed: 0x1e54ab27,
    yLeft: 432,
    yRight: 418,
    roughness: 0.5,
    roughnessDecay: 0.5,
    rowT: 0.36,
    peakSpan: 82,
    rockies: {
      peakSharpness: 1.08,
      envelopeStrength: 0.78,
      spikeIntensity: 0.12,
      spikeCount: 5,
      serration: 0.034,
    },
  },
  {
    seed: 0x9e3779b9,
    yLeft: 476,
    yRight: 463,
    roughness: 0.55,
    roughnessDecay: 0.48,
    rowT: 0.55,
    peakSpan: 94,
    rockies: {
      peakSharpness: 1.1,
      envelopeStrength: 0.82,
      spikeIntensity: 0.14,
      spikeCount: 5,
      serration: 0.04,
    },
  },
  {
    seed: 0xcafebabe,
    yLeft: 528,
    yRight: 518,
    roughness: 0.34,
    roughnessDecay: 0.54,
    rowT: 0.8,
    peakSpan: 48,
    rockies: {
      peakSharpness: 1.05,
      envelopeStrength: 0.68,
      spikeIntensity: 0.07,
      spikeCount: 4,
      serration: 0.022,
    },
  },
] as const;

const LAYERS = MOUNTAIN_RANGE_LAYERS;

/** Closed SVG fill paths, back to front, for the shared ridge layers. */
export function buildMountainRangePaths(): string[] {
  return LAYERS.map((layer) => {
    const ridge = diamondSquareRidge({
      width: W,
      exponent: GRID_EXPONENT,
      seed: layer.seed,
      roughness: layer.roughness,
      roughnessDecay: layer.roughnessDecay,
      rowT: layer.rowT,
      yLeft: layer.yLeft,
      yRight: layer.yRight,
      peakSpan: layer.peakSpan,
      rockies: layer.rockies,
    });
    const softened = smoothRidgeY(
      ridge,
      RIDGE_SMOOTH_PASSES,
      RIDGE_NEIGHBOR_BLEND
    );
    return ridgePathSmoothCurves(softened, W, H);
  });
}

type MountainRangeBackgroundProps = {
  className?: string;
};

export function MountainRangeBackground({
  className,
}: MountainRangeBackgroundProps) {
  const paths = useMemo(buildMountainRangePaths, []);

  return (
    <svg
      className={["mountain-range-background__svg", className]
        .filter(Boolean)
        .join(" ")}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      focusable="false"
    >
      {LAYERS.map((_, i) => (
        <path
          key={i}
          className={`mountain-layer mountain-layer--${i}`}
          d={paths[i]}
        />
      ))}
    </svg>
  );
}
